/*
 * Copyright © 2018 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschützt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulässig. Dies gilt
 * insbesondere für Vervielfältigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht übergeben, ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 */

import {ChangeDetectionStrategy, Component, computed, DestroyRef, OnInit, signal} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {take} from 'rxjs/operators';
import {LogFactory} from '../../core/logging/log-factory';
import {LohnausweisService} from '../../core/lohnausweis.service';
import {ClearAllDialogComponent} from '../../onboarding/clear-all-dialog/clear-all-dialog.component';
import {
    DialogMode,
    DownloadMode,
    PdfPrintAllDialogComponent,
} from '../pdf-print-all-dialog/pdf-print-all-dialog.component';
import {PrintingService} from '../printing.service';
import {SaveDataDialogComponent} from '../save-data-dialog/save-data-dialog.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

const LOG = LogFactory.createLog('PdfPrintComponent');

@Component({
    selector: 'elohn-pdf-print',
    templateUrl: './pdf-print.component.html',
    styleUrls: ['./pdf-print.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfPrintComponent implements OnInit {

    public readonly isPrintAllDisabled = computed(() => this.printingService.inProgress());
    private readonly printCurrentDisabled = signal(true);
    public readonly isPrintCurrentDisabled = computed(() =>
        this.printingService.inProgress() || this.printCurrentDisabled());

    constructor(
        private readonly lohnausweisService: LohnausweisService,
        private readonly printingService: PrintingService,
        private readonly dialog: MatDialog,
        private readonly router: Router,
        private readonly destroyRef: DestroyRef,
    ) {
    }

    public ngOnInit(): void {
        this.lohnausweisService.current$()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: next => {
                    LOG.debug('changing to lohnausweis: ', next);
                    this.printCurrentDisabled.set(!next);
                },
                error: (error: unknown) => LOG.error(error),
            });
    }

    public printAll(): void {
        SaveDataDialogComponent.open(this.dialog)
            .afterClosed()
            .subscribe({
                next: (print) => {
                    if (!print) {
                        return;
                    }
                    this.doPrint(false);
                },
                error: (err: unknown) => LOG.error(err),
            });
    }

    private doPrint(onlyCurrent: boolean): void {
        if (onlyCurrent) {
            this.printingService.printCurrent();
        } else {
            PdfPrintAllDialogComponent.open(this.dialog, DialogMode.ALL)
                .afterClosed()
                .subscribe({
                    next: (downloadMode) => {
                        if (downloadMode === DownloadMode.SINGLE_PDF) {
                            this.printingService.printAll();
                        } else if (downloadMode === DownloadMode.ZIP) {
                            this.printingService.printAllZipped();
                        } else {
                            LOG.error(`Not implemented: ${downloadMode}`);
                        }
                    },
                    error: (err: unknown) => LOG.error(err),
                });
        }
    }

    public printCurrent(): void {
        SaveDataDialogComponent.open(this.dialog)
            .afterClosed()
            .subscribe({
                next: (print) => {
                    if (!print) {
                        return;
                    }
                    this.doPrint(true);
                },
                error: (err: unknown) => LOG.error(err),
            });
    }

    public clearAll(): void {
        this.dialog.open(ClearAllDialogComponent).afterClosed()
            .pipe(take(1))
            .subscribe({
                next: next => next ? this.router.navigate(['/']) : LOG.debug('no data deleted'),
                error: (error: unknown) => LOG.error(error),
            });
    }
}
